import { Box, Breakpoint, Grid, GridSpan } from '@factorialco/deprecated-design-system'
import { format, parseISO } from 'date-fns'
import { BenefitBalancesFragment } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { calculateResetDate } from 'modules/benefits/lib/util'
import { BenefitBalanceCard } from 'modules/benefits/pages/MyBenefits/BenefitBalanceCard'

interface BenefitBalancesListProps {
  benefitBalancesList: BenefitBalancesFragment
}

function BenefitBalancesList({ benefitBalancesList }: BenefitBalancesListProps) {
  const { employee } = useCurrentSession()
  const country = employee?.legalEntity?.country || 'es'

  const balances =
    benefitBalancesList.benefitBalancesConnection.edges.map((edge) => edge.node) ?? []

  const mealsBalance = balances?.find((balance) => balance.category === 'meals')
  const transportBalance = balances?.find((balance) => balance.category === 'transportation')

  // Use the cycleResetDate from backend, fallback to current date calculation if not available
  const backendResetDate = mealsBalance?.cycleResetDate || transportBalance?.cycleResetDate

  const resetDate = backendResetDate
    ? parseISO(backendResetDate)
    : calculateResetDate(new Date(), country)

  const formattedResetDate = format(resetDate, 'MMM do')

  const gridColSpan: { [key in Breakpoint]?: GridSpan } = {
    xxs: 12,
    xs: 12,
    s: 12,
    m: 4,
    l: 4,
    xl: 3,
    xxl: 3,
  }

  return (
    <Box flexDirection="column" role="group">
      <Grid variant="fixed">
        <Box gridColSpan={gridColSpan}>
          <BenefitBalanceCard
            title={i18n.t('benefits.my_benefits.benefits.meals.title')}
            balance={mealsBalance?.availableCents ?? 0}
            icon="coffee"
            backgroundColor="radicalRed1000"
            limit={mealsBalance?.limitCents ?? 0}
            untilDate={formattedResetDate}
          />
        </Box>

        {transportBalance && (
          <Box gridColSpan={gridColSpan}>
            <BenefitBalanceCard
              title={i18n.t('benefits.my_benefits.benefits.transport.title')}
              balance={transportBalance?.availableCents ?? 0}
              icon="rocket"
              backgroundColor="lime1000"
              limit={transportBalance?.limitCents ?? 0}
              untilDate={formattedResetDate}
            />
          </Box>
        )}

        <Box gridColSpan={gridColSpan}>
          <Callout
            title={i18n.t('benefits.my_benefits.help_tip.spending_limit')}
            description={
              country === 'pt'
                ? i18n.t('benefits.my_benefits.help_tip.spending_limit_description_monthly_pt', {
                    resetDate: formattedResetDate,
                  })
                : i18n.t('benefits.my_benefits.help_tip.spending_limit_description_monthly', {
                    resetDate: formattedResetDate,
                  })
            }
            type="info"
          />
        </Box>
      </Grid>
    </Box>
  )
}

export { BenefitBalancesList }
